import { request } from './request'
import { useServerFetch } from '../composables/useServerFetch'
import { useEnvApi } from '../composables/useEnvApi'

const ASSET_RESERVE_LIST_API_PATH = '/api/v4/bit/web/trader/detail/asset-reserve/list'
const FUND_FLOW_LIST_API_PATH = '/api/v4/bit/web/trader/detail/fund-flow/list'
const TRADING_VOLUME_LIST_API_PATH = '/api/v4/bit/web/trader/detail/trading-volume/list'

export type TraderAssetReserveListItem = {
  icon?: string
  name?: string
  code?: string
  price?: string
  typeAmount?: string
  reserveAmount?: string
  networkName?: string
  networkIcon?: string
  address?: string
  maskedAddress?: string
}

export type TraderAssetReserveListParams = {
  exchangeId: string | number
  page: number
  size: number
}

export type TraderAssetReserveListResponse = {
  data: TraderAssetReserveListItem[]
  total: number
}

export type TraderFundFlowListItem = {
  logo?: string
  name?: string
  wallet?: string
  volume24hValue?: string
  volume24h?: number
  position?: string
}

export type TraderFundFlowListResponse = {
  header?: string[]
  data?: TraderFundFlowListItem[]
  total?: number
}

export type TraderFundFlowListParams = {
  exchangeId: string | number
  timeRangeType?: string | number
  page: number
  size: number
}

export type TraderTradingVolumeListParams = {
  exchangeId: string | number
  timeRangeType?: string | number
  subTabType?: number
  page: number
  size: number
}

export type TraderTradingVolumeListItem = {
  logoTopLeft?: string
  logoBottomRight?: string
  pairName?: string
  baseCurrencyName?: string
  volume24h?: string
  ratio?: string
  openInterest?: string
  expireTime?: string | number
  expiryTime?: string | number
}

export type TraderTradingVolumeListResponse = {
  data: TraderTradingVolumeListItem[]
  total: number
}

type TraderAssetReserveListRawResponse = {
  data?: {
    data?: TraderAssetReserveListItem[] | null
    list?: TraderAssetReserveListItem[] | null
    total?: number | string
  } | null
  list?: TraderAssetReserveListItem[] | null
  total?: number | string
} | null
type TraderFundFlowListRawResponse = TraderFundFlowListResponse | { data?: TraderFundFlowListResponse | null } | null
type TraderTradingVolumeListRawResponse = {
  data?:
    | TraderTradingVolumeListItem[]
    | {
        data?: TraderTradingVolumeListItem[] | null
        list?: TraderTradingVolumeListItem[] | null
        total?: number | string
      }
    | null
  list?: TraderTradingVolumeListItem[] | null
  total?: number | string
} | null

const isTraderFundFlowListResponse = (value: TraderFundFlowListRawResponse): value is TraderFundFlowListResponse =>
  Boolean(value && typeof value === 'object' && ('header' in value || 'data' in value || 'total' in value))

/**
 * 兼容 SSR 原始响应和客户端拦截器解包后的结构。
 * @param response 资金流向接口返回值。
 * @returns 页面消费的统一资金流向结构。
 */
const normalizeTraderFundFlowListResponse = (
  response: TraderFundFlowListRawResponse,
): TraderFundFlowListResponse | null => {
  if (!response) {
    return null
  }

  if (typeof response === 'object' && 'data' in response && !Array.isArray(response.data)) {
    return response.data ?? null
  }

  if (isTraderFundFlowListResponse(response)) {
    return response
  }

  return null
}

/**
 * 获取资产储备明细分页数据。
 * @param params 接口查询参数。
 * @returns 兼容 SSR 与客户端的资产储备分页结果。
 */
export const getTraderAssetReserveListPage = async ({
  exchangeId,
  page,
  size,
}: Readonly<TraderAssetReserveListParams>): Promise<TraderAssetReserveListResponse> => {
  const api = useEnvApi()
  const params = {
    exchangeId,
    page,
    size,
  }

  const response = import.meta.server
    ? await useServerFetch<TraderAssetReserveListRawResponse>({
        baseURL: api.wikibitBffGo,
        url: ASSET_RESERVE_LIST_API_PATH,
        method: 'GET' as const,
        query: params,
      })
    : await request<unknown, TraderAssetReserveListRawResponse>({
        baseURL: api.v4,
        url: ASSET_RESERVE_LIST_API_PATH,
        method: 'GET' as const,
        params,
      })
  const responseData = response?.data

  return {
    data: response?.list ?? responseData?.list ?? responseData?.data ?? [],
    total: Number(response?.total ?? responseData?.total ?? 0) || 0,
  }
}

/**
 * 获取资金流向明细数据。
 * @param params 接口查询参数。
 * @returns 兼容 SSR 与客户端的资金流向结果。
 */
export const getTraderFundFlowList = async ({
  exchangeId,
  timeRangeType,
  page,
  size,
}: Readonly<TraderFundFlowListParams>): Promise<TraderFundFlowListResponse | null> => {
  const api = useEnvApi()
  const params = {
    exchangeId,
    timeRangeType,
    page,
    size,
  }

  const response = import.meta.server
    ? await useServerFetch<TraderFundFlowListRawResponse>({
        baseURL: api.wikibitBffGo,
        url: FUND_FLOW_LIST_API_PATH,
        method: 'GET' as const,
        query: params,
      })
    : await request<unknown, TraderFundFlowListRawResponse>({
        baseURL: api.v4,
        url: FUND_FLOW_LIST_API_PATH,
        method: 'GET' as const,
        params,
      })

  return normalizeTraderFundFlowListResponse(response)
}

/**
 * 获取交易量明细列表，供 review 页顶部资产实力卡片直接消费。
 * @param params 接口查询参数。
 * @returns 当前卡片需要的交易量数组结果。
 */
export const getTraderTradingVolumeList = async ({
  exchangeId,
  timeRangeType,
  subTabType = 0,
  page,
  size,
}: Readonly<TraderTradingVolumeListParams>): Promise<TraderTradingVolumeListItem[]> => {
  const api = useEnvApi()
  const params = {
    exchangeId,
    timeRangeType,
    subTabType,
    page,
    size,
  }

  const response = import.meta.server
    ? await useServerFetch<TraderTradingVolumeListRawResponse>({
        baseURL: api.wikibitBffGo,
        url: TRADING_VOLUME_LIST_API_PATH,
        method: 'GET' as const,
        query: params,
      })
    : await request<unknown, TraderTradingVolumeListRawResponse>({
        baseURL: api.v4,
        url: TRADING_VOLUME_LIST_API_PATH,
        method: 'GET' as const,
        params,
      })
  const responseData = Array.isArray(response?.data) ? null : response?.data

  return (
    responseData?.data ?? responseData?.list ?? response?.list ?? (Array.isArray(response?.data) ? response.data : [])
  )
}

/**
 * 获取交易量明细分页数据。
 * @param params 接口查询参数。
 * @returns 兼容 SSR 与客户端的交易量分页结果。
 */
export const getTraderTradingVolumeListPage = async ({
  exchangeId,
  timeRangeType,
  subTabType = 0,
  page,
  size,
}: Readonly<TraderTradingVolumeListParams>): Promise<TraderTradingVolumeListResponse> => {
  const api = useEnvApi()
  const params = {
    exchangeId,
    timeRangeType,
    subTabType,
    page,
    size,
  }

  const response = import.meta.server
    ? await useServerFetch<TraderTradingVolumeListRawResponse>({
        baseURL: api.wikibitBffGo,
        url: TRADING_VOLUME_LIST_API_PATH,
        method: 'GET' as const,
        query: params,
      })
    : await request<unknown, TraderTradingVolumeListRawResponse>({
        baseURL: api.v4,
        url: TRADING_VOLUME_LIST_API_PATH,
        method: 'GET' as const,
        params,
      })
  const responseData = Array.isArray(response?.data) ? null : response?.data

  return {
    data:
      responseData?.data ??
      responseData?.list ??
      response?.list ??
      (Array.isArray(response?.data) ? response.data : []),
    total: Number(responseData?.total ?? response?.total ?? 0) || 0,
  }
}
